import { motion, AnimatePresence, useInView, stagger, useAnimate } from 'framer-motion';
import { StackedContent, MotionImage, useSanitizedTranslation, TextBlock } from 'ui';
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useWindowSize } from '@uidotdev/usehooks';

export interface Icon {
  icon: string;
  title: string;
  body: string;
}

export interface IconAnchorProps {
  title: any;
  icons: Icon[];
  content: any,
  images: string [] | string;
  imgshadow: boolean;
  bg: string;
}

export interface StickyTextProps {
  item: any;
  trigger: any;
  state: number;
  index: number;
  image: string;
  bg: string;
}

function getElementTopPosition(elem: any) {
  var box = elem.getBoundingClientRect();

  var body = document.body;
  var docEl = document.documentElement;

  var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;

  var clientTop = docEl.clientTop || body.clientTop || (box.height/2.5);

  var top = box.top + scrollTop + clientTop;

  return top;
}


const StickyText = ({ item, trigger, state, index, image, bg="transparent" }: StickyTextProps) => {
  const ref = useRef();
  const isInView = useInView(ref, { amount: 0, margin: '-40% 0% -40% 0%' });

  useEffect(() => {
    if (state !== index && isInView) {
      trigger(index);
    }
  }, [isInView, state, trigger]);

  // media query
  const { width: windowWidth} = useWindowSize();
  const [isSm, setIsSm] = useState(false)

  useEffect(()=> {
    if (windowWidth && windowWidth < 1100) {
      setIsSm(true)
    } else {
      setIsSm(false)
    }
  }, [windowWidth])

  return (
    <div ref={ref} className="relative z-10 flex flex-col items-center text-center max-lg:py-8 lg:-translate-y-1/2 2xl:last-of-type:lg:translate-y-[0%]">
      <motion.div id={'sticky' + index} className='flex flex-col items-center justify-center gap-5 lg:gap-0 p-10 lg:p-0 rounded-lg lg:rounded-none lg:translate-y-[30%]' style={{background: (isSm && bg) ? bg : 'transparent'}}>
        <div className='h-auto w-[75%] p-5 block lg:hidden'>
          <Image
            src={image} 
            alt="icon"
            width={500} 
            height={500}
            priority={true}
          />
        </div>
        <StackedContent content={item} left minHeight />
      </motion.div>
    </div>
  );
};

const IconButton = ({index, className, icon="", title="", body=""}) => {   
  const { t } = useSanitizedTranslation();
    return(
        <motion.button
            onClick={() => window.scrollTo(0, getElementTopPosition(document.getElementById('sticky'+index)) - (window.innerHeight / 1.85))}
            className={`group flex grow basis-1 flex-col items-center justify-between h-full rounded-xl p-2 text-center lg:basis-0 lg:gap-y-8 ${className}`}
            initial={{opacity: 0}}
        >
            <div className="aspect-square w-[30px] lg:w-[55px]">
                <img src={icon} className="h-full w-full" />
            </div>
            <div className='flex flex-col items-center justify-center gap-3'>
                <p
                data-i18n={title}
                className="type-body flex grow text-center max-lg:my-4"
                dangerouslySetInnerHTML={{ __html: t(title) }}
                ></p>
                <p
                data-i18n={body}
                className="flex grow text-center max-lg:mb-4"
                dangerouslySetInnerHTML={{ __html: t(body) }}
                ></p>
            </div>
            <svg
            width="24"
            height="24"
            viewBox="0 0 38 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="relative -top-2 -scale-y-100 transition-transform group-hover:translate-y-[.25rem] [&>path]:transition-colors group-hover:[&>path]:stroke-[#8C557F]"
            >
                <path d="M34.5278 20.0776L18.3585 4.33431L2.74039 20.6754" stroke="black" strokeWidth="5" strokeLinecap="round" />
            </svg>
        </motion.button>
    )
}

export const IconAnchorLink = ({ imgshadow, content, icons, title, images, bg}: IconAnchorProps) => {

    const [activeState, setState] = useState(0);

     const [ref, animate] = useAnimate();
      const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });
    
      useEffect(() => {
        if (inView) {
          animate(
            'button',
            {
              y: [30, 0],
              opacity: [0, 1],
            },
            {
              ease: [0, 1, 1, 1],
              duration: 0.8,
              delay: stagger(0.15),
              deltaRest: 0.000001,
              deltaSpeed: 0.000001,
            },
          );
        }
      }, [inView]);

    return (
        <motion.section ref={ref} className="w-full relative z-40 items-center bg-white lg:pb-28 xl:pb-40" data-testid={'text-section'}>
        {/* Title */}
        <div className='w-full flex items-center mb-10 px-10 max-w-[1575px] mx-auto'>
            <StackedContent 
                full
                center
                content={title} 
            />
        </div>
        {/* Icons */}
        <motion.div className="content-wrapper-x mb-16 flex flex-wrap items-stretch justify-between gap-4 lg:mb-40 lg:pb-[150px] xl:pb-[200px] 2xl:pb-[240px] max-w-[1600px] mx-auto">
            {icons.map((item, index) => (
                <IconButton
                    index={index}
                    className=""
                    icon={item.icon}
                    title={item.title}
                    body={item.body}
                />
            ))}
        </motion.div>
        {/* Sticky Image and Text */}
        <div className="wrapper lg:-mb-[150px] xl:-mb-[200px] 2xl:-mb-[240px]">
            <div className="content-wrapper-x mb-32">
            <div className="flex gap-8">
                <div className={`flex items-start justify-center max-lg:hidden lg:basis-3/5`}>
                <div className="top-[calc(50vh)] -mt-4 flex w-full items-center justify-center lg:sticky">
                    <motion.div
                    style={{
                        filter: imgshadow ? 'drop-shadow(10px 10px 3px rgba(0,0,0,0.15)) drop-shadow(1px 2px 2px rgba(0,0,0,0.75))' : 'none',
                    }}
                    className="relative lg:-left-[15%] lg:-translate-y-[50%] w-full aspect-video flex items-center justify-center"
                    >
                        <div className="absolute left-[1%] -top-[11vh] xl:-top-[9vh] 2xl:-top-[12vh] w-[98%] aspect-square">
                            <AnimatePresence>
                            <MotionImage
                                fill
                                sizes="100vw"
                                className="h-auto w-[75%] left-0 right-0 top-0 bottom-0 m-auto"
                                alt=""
                                key={'tvsticky' + activeState}
                                src={images[activeState]}
                                initial={{ opacity: 0 }}
                                animate={{ zIndex: 10, opacity: 1, transition: { ease: 'easeInOut', duration: 0.5 } }}
                                exit={{ opacity: 0, zIndex: 1, transition: { ease: 'easeInOut',duration: 0.1, delay: 0.1 } }}
                                priority={true}
                            />
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
                </div>

                <div className="relative z-10 flex w-full flex-col items-center text-center lg:basis-2/5 lg:space-y-[70vh] lg:pb-[25vh]">
                {content.map((item: any, index: number) => (
                    <StickyText key={'tv-sticky-' + index} item={item} state={activeState} index={index} trigger={() => setState(index)} image={images[index]} bg={bg}/>
                ))}
                </div>
            </div>
            </div>
        </div>
        </motion.section>
    );
};